// frontend/src/components/Provider/BusinessProfileForm.tsx
// UPDATED: Added cancel support and better edit mode handling

import React from 'react';
import { 
  Save,
  AlertCircle,
  User,
  Building,
  Clock,
  FileText,
  Phone,
  Mail,
  Globe,
  Award,
  Shield,
  X
} from 'lucide-react';

interface BusinessProfile {
  business_name: string;
  description: string;
  years_experience: number;
  business_type: string;
  business_license: string;
  insurance_details: string;
  website: string;
  business_phone: string;
  business_email: string;
  about_me: string;
  specializations: string[];
  certifications: string[];
  // Additional fields to match BusinessProfile page
  address?: string;
  city?: string;
  provider_slug_url?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
}

interface BusinessProfileFormProps {
  profileData: BusinessProfile;
  onDataChange: (data: BusinessProfile) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  errors: Record<string, string>;
  success: boolean;
  submitButtonText?: string;
  showQuickActions?: boolean;
  onCancel?: () => void; // NEW: Optional cancel handler
}

const BusinessProfileForm: React.FC<BusinessProfileFormProps> = ({
  profileData,
  onDataChange,
  onSubmit,
  isLoading,
  errors,
  success,
  submitButtonText = "Save Profile",
  showQuickActions = false,
  onCancel // NEW: Cancel handler
}) => {
  // Business types
  const businessTypes = [
    { value: 'individual', label: 'Individual Service Provider' },
    { value: 'small_business', label: 'Small Business' },
    { value: 'company', label: 'Company' },
    { value: 'llc', label: 'LLC' },
    { value: 'corporation', label: 'Corporation' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    onDataChange({
      ...profileData,
      [name]: value
    });
  };

  const handleArrayInputChange = (field: 'specializations' | 'certifications', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    onDataChange({
      ...profileData,
      [field]: items
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header with Cancel Button */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </button>
        )}
      </div>
      
      {/* General error message */}
      {errors.general && (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{errors.general}</div>
            </div>
          </div>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="mb-6 rounded-md bg-green-50 border border-green-200 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Profile updated successfully!
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Basic Business Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Name */}
          <div>
            <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="inline h-4 w-4 mr-1" />
              Business Name *
            </label>
            <input
              type="text"
              id="business_name"
              name="business_name"
              value={profileData.business_name}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.business_name ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="Enter your business name"
              required
            />
            {errors.business_name && (
              <p className="mt-1 text-sm text-red-600">{errors.business_name}</p>
            )}
          </div>

          {/* Business Type */}
          <div>
            <label htmlFor="business_type" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-4 w-4 mr-1" />
              Business Type *
            </label>
            <select
              id="business_type"
              name="business_type"
              value={profileData.business_type}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.business_type ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              required
            >
              {businessTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {errors.business_type && (
              <p className="mt-1 text-sm text-red-600">{errors.business_type}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            <FileText className="inline h-4 w-4 mr-1" />
            Business Description *
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            value={profileData.description}
            onChange={handleInputChange}
            className={`block w-full rounded-md border ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
            placeholder="Describe your business and services..."
            required
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Years Experience */}
          <div>
            <label htmlFor="years_experience" className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline h-4 w-4 mr-1" />
              Years of Experience *
            </label>
            <input
              type="number"
              id="years_experience"
              name="years_experience"
              min="1"
              max="50"
              value={profileData.years_experience}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.years_experience ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              required
            />
            {errors.years_experience && (
              <p className="mt-1 text-sm text-red-600">{errors.years_experience}</p>
            )}
          </div>

          {/* Business Phone */}
          <div>
            <label htmlFor="business_phone" className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="inline h-4 w-4 mr-1" />
              Business Phone
            </label>
            <input
              type="tel"
              id="business_phone"
              name="business_phone"
              value={profileData.business_phone}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.business_phone ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="Your business phone number"
            />
            {errors.business_phone && (
              <p className="mt-1 text-sm text-red-600">{errors.business_phone}</p>
            )}
          </div>

          {/* Business Email */}
          <div>
            <label htmlFor="business_email" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Business Email
            </label>
            <input
              type="email"
              id="business_email"
              name="business_email"
              value={profileData.business_email}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.business_email ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="your.business@email.com"
            />
            {errors.business_email && (
              <p className="mt-1 text-sm text-red-600">{errors.business_email}</p>
            )}
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1" />
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={profileData.website}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.website ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="https://www.yourbusiness.com"
            />
            {errors.website && (
              <p className="mt-1 text-sm text-red-600">{errors.website}</p>
            )}
          </div>
        </div>

        {/* About Me */}
        <div>
          <label htmlFor="about_me" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline h-4 w-4 mr-1" />
            About Me *
          </label>
          <textarea
            id="about_me"
            name="about_me"
            rows={4}
            value={profileData.about_me}
            onChange={handleInputChange}
            className={`block w-full rounded-md border ${errors.about_me ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
            placeholder="Tell potential customers about yourself and your experience..."
            required
          />
          {errors.about_me && (
            <p className="mt-1 text-sm text-red-600">{errors.about_me}</p>
          )}
        </div>

        {/* Business Credentials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business License */}
          <div>
            <label htmlFor="business_license" className="block text-sm font-medium text-gray-700 mb-2">
              <Shield className="inline h-4 w-4 mr-1" />
              Business License
            </label>
            <input
              type="text"
              id="business_license"
              name="business_license"
              value={profileData.business_license}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.business_license ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="License number or details"
            />
            {errors.business_license && (
              <p className="mt-1 text-sm text-red-600">{errors.business_license}</p>
            )}
          </div>

          {/* Insurance Details */}
          <div>
            <label htmlFor="insurance_details" className="block text-sm font-medium text-gray-700 mb-2">
              <Shield className="inline h-4 w-4 mr-1" />
              Insurance Details
            </label>
            <input
              type="text"
              id="insurance_details"
              name="insurance_details"
              value={profileData.insurance_details}
              onChange={handleInputChange}
              className={`block w-full rounded-md border ${errors.insurance_details ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
              placeholder="Insurance provider and policy details"
            />
            {errors.insurance_details && (
              <p className="mt-1 text-sm text-red-600">{errors.insurance_details}</p>
            )}
          </div>
        </div>

        {/* Specializations */}
        <div>
          <label htmlFor="specializations" className="block text-sm font-medium text-gray-700 mb-2">
            <Award className="inline h-4 w-4 mr-1" />
            Specializations
          </label>
          <input
            type="text"
            id="specializations"
            value={profileData.specializations?.join(', ') || ''}
            onChange={(e) => handleArrayInputChange('specializations', e.target.value)}
            className={`block w-full rounded-md border ${errors.specializations ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
            placeholder="Plumbing, Electrical, HVAC, Roofing (comma separated)"
          />
          <p className="mt-1 text-xs text-gray-500">Separate multiple specializations with commas</p>
          {errors.specializations && (
            <p className="mt-1 text-sm text-red-600">{errors.specializations}</p>
          )}
        </div>

        {/* Certifications */}
        <div>
          <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-2">
            <Award className="inline h-4 w-4 mr-1" />
            Certifications
          </label>
          <input
            type="text"
            id="certifications"
            value={profileData.certifications?.join(', ') || ''}
            onChange={(e) => handleArrayInputChange('certifications', e.target.value)}
            className={`block w-full rounded-md border ${errors.certifications ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
            placeholder="EPA Certified, Master Plumber, Licensed Electrician (comma separated)"
          />
          <p className="mt-1 text-xs text-gray-500">Separate multiple certifications with commas</p>
          {errors.certifications && (
            <p className="mt-1 text-sm text-red-600">{errors.certifications}</p>
          )}
        </div>

        {/* Location Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Location Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={profileData.address || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.address ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="Street address"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={profileData.city || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.city ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="City"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={profileData.first_name || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.first_name ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="Your first name"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={profileData.last_name || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="Your last name"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            {/* Personal Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Personal Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Personal Phone */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                Personal Phone
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={profileData.phone_number || ''}
                onChange={handleInputChange}
                className={`block w-full rounded-md border ${errors.phone_number ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone_number && (
                <p className="mt-1 text-sm text-red-600">{errors.phone_number}</p>
              )}
            </div>

            {/* Preferred Language */}
            <div>
              <label htmlFor="preferred_language" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Language
              </label>
              <select
                id="preferred_language"
                name="preferred_language"
                value={profileData.preferred_language || 'en'}
                onChange={handleInputChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="pt">Portuguese</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3 pt-6">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                Saving Profile...
              </>
            ) : (
              <>
                <Save className="mr-2 h-5 w-5" />
                {submitButtonText}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessProfileForm;